import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { financialAPI } from '../lib/api';
import { 
  DollarSign, TrendingUp, CreditCard, BarChart3, 
  Download, Calendar, ArrowUp, ArrowDown, Loader2,
  PieChart, Clock, Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

export default function AdminFinancialPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [projections, setProjections] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [commissions, setCommissions] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewRes, dailyRes, projectionsRes, txRes, commRes] = await Promise.all([
        financialAPI.overview(),
        financialAPI.dailyRevenue(30),
        financialAPI.projections(),
        financialAPI.transactions({ limit: 10 }),
        financialAPI.commissions(),
      ]);
      setOverview(overviewRes.data);
      setDailyData(dailyRes.data.data || []);
      setProjections(projectionsRes.data);
      setTransactions(txRes.data.transactions || []);
      setCommissions(commRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load financial data');
    }
    setLoading(false);
  };

  const exportData = async (type) => {
    try {
      const res = await financialAPI.export({ report_type: type });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xdomainx-${type}-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" />
      </div>
    );
  }

  const maxRevenue = Math.max(...dailyData.map(d => d.revenue), 1);

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-8" data-testid="admin-financial-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Outfit']">Financial Reports</h1>
          <p className="text-zinc-500 mt-1">Revenue analytics and financial overview</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => exportData('transactions')} variant="outline" size="sm" className="border-zinc-700 text-zinc-300">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-zinc-500 text-sm">Total Revenue</p>
                <p className="text-2xl font-bold text-white">${overview?.total_revenue?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#00E5FF]/10 rounded-lg">
                <TrendingUp className="h-6 w-6 text-[#00E5FF]" />
              </div>
              <div>
                <p className="text-zinc-500 text-sm">Monthly Revenue</p>
                <p className="text-2xl font-bold text-white">${overview?.monthly_revenue?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <CreditCard className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <p className="text-zinc-500 text-sm">Pending Revenue</p>
                <p className="text-2xl font-bold text-white">${overview?.pending_revenue?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-500/10 rounded-lg">
                <BarChart3 className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <p className="text-zinc-500 text-sm">Total Transactions</p>
                <p className="text-2xl font-bold text-white">{overview?.total_transactions || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart & Projections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="bg-[#0A0A0A] border-white/5 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#00E5FF]" />
              Daily Revenue (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-end gap-1">
              {dailyData.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center group">
                  <div className="relative w-full">
                    <div 
                      className="w-full bg-[#00E5FF]/20 hover:bg-[#00E5FF]/40 transition-colors rounded-t"
                      style={{ height: `${Math.max((day.revenue / maxRevenue) * 200, 4)}px` }}
                    />
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-zinc-800 px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      ${day.revenue.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-zinc-500 mt-2">
              <span>{dailyData[0]?.date || ''}</span>
              <span>{dailyData[dailyData.length - 1]?.date || ''}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="h-5 w-5 text-green-500" />
              Projections
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-zinc-900/50 rounded-lg">
              <p className="text-zinc-500 text-sm">Monthly Projection</p>
              <p className="text-xl font-bold text-green-500">${projections?.projections?.monthly?.toLocaleString() || 0}</p>
            </div>
            <div className="p-4 bg-zinc-900/50 rounded-lg">
              <p className="text-zinc-500 text-sm">Quarterly Projection</p>
              <p className="text-xl font-bold text-[#00E5FF]">${projections?.projections?.quarterly?.toLocaleString() || 0}</p>
            </div>
            <div className="p-4 bg-zinc-900/50 rounded-lg">
              <p className="text-zinc-500 text-sm">Yearly Projection</p>
              <p className="text-xl font-bold text-purple-500">${projections?.projections?.yearly?.toLocaleString() || 0}</p>
            </div>
            <div className="pt-2 border-t border-white/5">
              <p className="text-xs text-zinc-500">Active Auction Value</p>
              <p className="text-lg font-semibold text-white">${projections?.pipeline?.active_auction_value?.toLocaleString() || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Type & Commissions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PieChart className="h-5 w-5 text-purple-500" />
              Revenue by Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {overview?.revenue_by_type?.map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${['bg-[#00E5FF]', 'bg-green-500', 'bg-purple-500', 'bg-amber-500'][i % 4]}`} />
                    <span className="text-zinc-300 capitalize">{item.type}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">${item.total.toLocaleString()}</p>
                    <p className="text-zinc-500 text-xs">{item.count} transactions</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              Brokerage Commissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <p className="text-zinc-500 text-sm">Total Commission</p>
                <p className="text-xl font-bold text-green-500">${commissions?.summary?.total_commission?.toLocaleString() || 0}</p>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <p className="text-zinc-500 text-sm">Deal Volume</p>
                <p className="text-xl font-bold text-white">${commissions?.summary?.total_volume?.toLocaleString() || 0}</p>
              </div>
            </div>
            <div className="text-sm text-zinc-400">
              <p>{commissions?.summary?.deal_count || 0} completed deals</p>
              <p>Avg. commission: ${commissions?.summary?.avg_commission?.toLocaleString() || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#00E5FF]" />
            Recent Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Type</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Domain</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Amount</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 px-4 text-zinc-300 capitalize">{tx.type}</td>
                    <td className="py-3 px-4 text-white font-medium">{tx.domain_name}</td>
                    <td className="py-3 px-4 text-green-500 font-semibold">${tx.amount?.toLocaleString()}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        tx.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        tx.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {tx.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-zinc-500 text-sm">{tx.created_at?.split('T')[0]}</td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-zinc-500">No transactions yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
